import { Api } from "@penn/web-common";
import { unwrap } from "@penn/web-common";

export type FastenerBrandCode = "PEM" | "SHEREX" | "PROFIL" | "AUTORIV" | "OTHER";

export interface ApplicationFastenerLine {
  fastenerId: string;
  brand: FastenerBrandCode | null;
  type: string | null;
  qtyPerPart: number | null;
  qtyPerPartA: number | null;
  qtyPerPartB: number | null;
}

export interface ApplicationFastenerLineInput {
  fastenerId?: string;
  brand: FastenerBrandCode;
  type?: string | null;
  qtyPerPart?: number | null;
  qtyPerPartA?: number | null;
  qtyPerPartB?: number | null;
}

export interface ApplicationSummary {
  applicationId: string;
  name: string | null;
  applicationNumber: string | null;
  oem: string | null;
  productionProgram: string | null;
  materialCode: string | null;
  annualVolume: number | null;
  fastenerCount: number;
  createdByEmail: string | null;
  // Owner's company name (joined from the owning user); shown as the app's
  // company. Null when the owner has no company on record.
  ownerCompanyName?: string | null;
  // Creator's email (the staff impersonator on registered Work-For); null for
  // self / unregistered, where the creator is the owner.
  createdByUserEmail?: string | null;
  // Work-For "Other" (unregistered) customer email; shown as the Owner in place
  // of createdByEmail when present. Null for self + registered Work-For.
  workingForEmail?: string | null;
  has3d?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationDetail extends ApplicationSummary {
  batchSize: number | null;
  createdBy: string | null;
  fasteners: ApplicationFastenerLine[];
}

export interface PaginatedApplications {
  items: ApplicationSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListMyApplicationsQuery {
  limit?: number;
  offset?: number;
}

export interface CreateApplicationInput {
  name: string;
  applicationNumber?: string | null;
  oem?: string | null;
  productionProgram?: string | null;
  materialCode?: string | null;
  annualVolume?: number | null;
  batchSize?: number | null;
  // Work-For (XOR): registered target owns the app; "Other" keeps the creator
  // as owner and stores the customer email. Honoured only for canWorkForOthers.
  workForUserId?: string | null;
  workForEmail?: string | null;
  fasteners: ApplicationFastenerLineInput[];
}

export type UpdateApplicationInput = Partial<
  Omit<CreateApplicationInput, "workForUserId" | "workForEmail">
>;

export async function listMyApplications(
  query: ListMyApplicationsQuery = {},
): Promise<PaginatedApplications> {
  const res = await Api.get("/v1/applications/mine", {
    params: { limit: query.limit ?? 50, offset: query.offset ?? 0 },
  });
  return unwrap<PaginatedApplications>(res.data);
}

// Page size for the "fetch every row" helper. The server caps a single page, so
// we loop offsets until the list is exhausted rather than requesting an
// unbounded limit in one query.
const FETCH_ALL_PAGE_SIZE = 1000;

/**
 * Issue #8: return EVERY saved application (no page cap) by paging through
 * `/v1/applications/mine` until exhausted, so the modal shows the full list.
 */
export async function listAllMyApplications(): Promise<PaginatedApplications> {
  const items: ApplicationSummary[] = [];
  let offset = 0;
  let total = 0;
  for (let page = 0; page < 1000; page++) {
    const res = await listMyApplications({ limit: FETCH_ALL_PAGE_SIZE, offset });
    items.push(...res.items);
    total = res.total;
    if (res.items.length < FETCH_ALL_PAGE_SIZE || items.length >= res.total) break;
    offset += FETCH_ALL_PAGE_SIZE;
  }
  return { items, total: total || items.length, limit: items.length, offset: 0 };
}

export async function getApplication(
  id: string,
  signal?: AbortSignal,
): Promise<ApplicationDetail> {
  const res = await Api.get(`/v1/applications/${id}`, { signal });
  return unwrap<ApplicationDetail>(res.data);
}

export async function createApplication(
  body: CreateApplicationInput,
): Promise<ApplicationDetail> {
  const res = await Api.post("/v1/applications", body);
  return unwrap<ApplicationDetail>(res.data);
}

export async function updateApplication(
  id: string,
  body: UpdateApplicationInput,
): Promise<ApplicationDetail> {
  const res = await Api.patch(`/v1/applications/${id}`, body);
  return unwrap<ApplicationDetail>(res.data);
}

export async function deleteApplication(id: string): Promise<void> {
  await Api.delete(`/v1/applications/${id}`);
}
